// router.tsx
import { createBrowserRouter, Navigate } from "react-router-dom";
import { lazy } from "react";
import PrivateRoute from "./pages/PrivateRoute";
import ProjectPageTimeline from "./pages/project/ProjectPageTimeline";
import TestHome from "./pages/testWithoutAccount/TestHome";

/** Lazy pages/layouts (one file per import you had before) */
const ProjectPageSettings = lazy(
  () => import("./pages/project/ProjectPageSettings"),
);

const ProjectTasks = lazy(() => import("./pages/projects/ProjectTasks"));

const ProjectsRouter = lazy(() => import("./pages/projects/ProjectsRouter"));

const ArchivedTaskPage = lazy(
  () => import("./pages/archivedTasks/ArchivedTaskPage"),
);
const ProjectTogetherPage = lazy(
  () => import("./pages/project/ProjectTogetherPage"),
);

const CollectRouter = lazy(() => import("./pages/collect/CollectRouter"));

const DeleteAccountData = lazy(
  () => import("./pages/authPages/DeleteAccountData"),
);
const RequestDeleteAccountData = lazy(
  () => import("./pages/authPages/RequestDeleteAccountData"),
);
const DeskPage = lazy(() => import("./pages/home/DeskPage"));

const KanbanPage = lazy(() => import("./pages/project/KanbanPage"));
const ProjectHome = lazy(() => import("./pages/project/ProjectHome"));
const CollectionRoutes = lazy(
  () => import("./pages/collection/CollectionRoutes"),
);
const SettingsRoutes = lazy(() => import("./pages/settings/SettingsRoutes"));

const VerifiyAccountPage = lazy(
  () => import("./pages/authPages/VerifiyAccountPage"),
);
const RequestPasswordResetPage = lazy(
  () => import("./pages/authPages/RequestPasswordResetPage"),
);
const ResetPasswordPage = lazy(
  () => import("./pages/authPages/ResetPasswordPage"),
);
const LinkAccountPage = lazy(() => import("./pages/authPages/LinkAccountPage"));
const RolesPage = lazy(() => import("./pages/roles/RolesPage"));
const RequestRoutes = lazy(() => import("./pages/request/RequestRoutes"));
const WelcomePage = lazy(() => import("./pages/welcomePages/WelcomePage"));
const AuthPageRouter = lazy(() => import("./pages/authPages/AuthPageRouter"));
const HomeRoutes = lazy(() => import("./pages/home/HomeRoutes"));
const Layout = lazy(() => import("./Layout"));
const ErrorPage = lazy(() => import("./ErrorPage"));
const CalendarsPage = lazy(() => import("./pages/schedule/CalendarsPage"));
const ScheduleLayout = lazy(() => import("./pages/schedule/ScheduleRouter"));
const TaskListSchedule = lazy(
  () => import("./pages/schedule/TaskListSchedule"),
);
const TodayPage = lazy(() => import("./pages/schedule/TodayPage"));
const WeekPage = lazy(() => import("./pages/schedule/WeekPage"));
const RoutinesPage = lazy(() => import("./pages/schedule/RoutinesPage"));

const TaskPageTogether = lazy(() => import("./pages/task/TaskPageTogether"));
const TaskPageDo = lazy(() => import("./pages/task/TaskPageDo"));
const TaskPageInfo = lazy(() => import("./pages/task/TaskPageInfo"));
const TaskPageSchedule = lazy(() => import("./pages/task/TaskPageSchedule"));
const TaskLayout = lazy(() => import("./pages/task/TaskRoutes"));
const TaskPageCollect = lazy(() => import("./pages/task/taskPageCollect"));

const OAuthFinish = lazy(() => import("./pages/outhRedirects/OAuthFinish"));
const OAuthCallback = lazy(() => import("./pages/outhRedirects/OAuthCallback"));
const DelegateLayout = lazy(() => import("./pages/delegate/DelegatePage"));
const DelegateContent = lazy(() => import("./pages/delegate/DelegateContent"));
const RequestsContent = lazy(() => import("./pages/delegate/RequestsContent"));
const CarryOutLayout = lazy(() => import("./pages/action/ActionPageRoutes"));
const CuratedListPage = lazy(() => import("./pages/action/PriorityListPage"));
const TaskListKanban = lazy(() => import("./pages/action/TaskListKanban"));
const UrgencyListPage = lazy(() => import("./pages/action/UrgencyListPage."));
const WorkloadListPage = lazy(() => import("./pages/action/WorkloadListPage"));
const MatrixListPage = lazy(() => import("./pages/action/MatrixListPage"));
const ImportanceListPage = lazy(
  () => import("./pages/action/ImportanceListPage"),
);

const ArchivePage = lazy(() => import("./pages/archive/ArchivePage"));
const CollectionList = lazy(() => import("./pages/collect/CollectionList"));
const ArchiveRouter = lazy(() => import("./pages/archive/ArchiveRouter"));
const DeletedPage = lazy(() => import("./pages/archive/DeletedPage"));

const CompletedPage = lazy(() => import("./pages/archive/CompletedPage"));
const ReflectPage = lazy(() => import("./pages/archive/reflect/ReflectPage"));

const RoleTeamPage = lazy(() => import("./pages/roles/RoleTeamPage"));
const OrganizationAdminPage = lazy(
  () => import("./pages/organization/OrganizationSettingsPage"),
);
const OrganizationLayout = lazy(
  () => import("./pages/organization/OrganizationRoutes"),
);
const RoleOverviewPage = lazy(() => import("./pages/roles/RoleOverviewPage"));
const RoleProjectsPage = lazy(() => import("./pages/roles/RoleProjectsPage"));
const ObjectivesPage = lazy(() => import("./pages/objectives/ObjectivesPage"));
const ObjectiveDetailPage = lazy(
  () => import("./pages/objectives/ObjectiveDetailPage"),
);
const PersonalObjectivesLayout = lazy(
  () => import("./pages/objectives/PersonalObjectivesLayout"),
);

const GroupLayout = lazy(() => import("./pages/group/GroupRoutes"));
const GroupSettings = lazy(() => import("./pages/group/GroupSettings"));

const UserPage = lazy(() => import("./pages/profile/UserPage"));
const LoginPage = lazy(() => import("./pages/authPages/LoginPage"));

const CardStackLayout = lazy(() => import("./pages/cardStack/CardStackLayout"));
const CardStackPage = lazy(() => import("./pages/cardStack/CardStackPage"));

const DaexWall = lazy(() => import("./pages/pinwall/DaexWall"));
const RegisterPage = lazy(() => import("./pages/authPages/RegisterPage"));
const ProfilePage = lazy(() => import("./pages/profile/ProfilePage"));
const CalendarItemPage = lazy(
  () => import("./pages/calendarItem/CalendarItemPage"),
);
const ProjectRoutes = lazy(() => import("./pages/project/ProjectRoutes"));

export const RoutePath = {
  Home: "/",
  Login: "/login",
  Register: "/register",
  VerifyAccount: "/verify-account",
  RequestPasswordReset: "/request-password",
  ResetPassword: "/password-reset",
  TaskInfo: (taskId: string) => `/task/${taskId}`,
  TaskDo: (taskId: string) => `/task/${taskId}/do`,
  OrganizationSettings: (orgId: string) => `/organization/${orgId}/settings`,
} as const;

export function makeRouter() {
  return createBrowserRouter(
    [
      {
        path: RoutePath.Home,
        element: <Layout />, // 👈 wrap routes here
        errorElement: <ErrorPage />, // 👈 catch top-level errors
        children: [
          { path: "/oauth/callback/", element: <OAuthCallback /> }, // For oauth flow
          { path: "/oauth/finish/", element: <OAuthFinish /> }, // For oauth flow
          {
            element: <AuthPageRouter />,
            children: [
              { path: "register", element: <RegisterPage /> },
              { path: "login", element: <LoginPage /> },
            ],
          },
          { path: "/link-account", element: <LinkAccountPage /> },
          {
            path: "/delete-account",
            element: <DeleteAccountData></DeleteAccountData>,
          },
          {
            path: "/request-account-deletion",
            element: <RequestDeleteAccountData></RequestDeleteAccountData>,
          },
          { path: "/request-password", element: <RequestPasswordResetPage /> },
          { path: "/password-reset", element: <ResetPasswordPage /> },
          {
            path: "test",
            children: [{ path: "home", element: <TestHome></TestHome> }],
          },
          {
            element: <PrivateRoute />,
            children: [
              {
                path: "",
                element: <HomeRoutes />,
                children: [
                  {
                    index: true,
                    element: <DeskPage />,
                  },
                ],
              },
              {
                index: true,
                path: "/wall",
                element: <DaexWall />,
              },
              {
                path: "stack/:viewedNumber?",
                element: <CardStackLayout />,
                children: [
                  {
                    index: true,
                    element: <CardStackPage />,
                  },
                ],
              },
              // Instructions and Onboarding
              {
                path: "welcome/*",
                element: <WelcomePage />,
                /*    loader: () => {
                  if (sessionStorage.getItem("hasSeenWelcome") === "true") {
                    return redirect("/");
                    // or: return redirect("/wall");
                  }
                  return null;
                },
 */
              },
              {
                path: "action",
                element: <CarryOutLayout />,
                children: [
                  {
                    index: true,
                    element: <MatrixListPage />,
                  },
                  {
                    path: "priority",
                    element: <CuratedListPage />,
                  },
                  {
                    path: "progress",
                    element: <TaskListKanban />,
                  },
                  {
                    path: "urgency",
                    element: <UrgencyListPage />,
                  },
                  {
                    path: "importance",
                    element: <ImportanceListPage />,
                  },
                  {
                    path: "workload",
                    element: <WorkloadListPage />,
                  },
                ],
              },
              { path: "/settings", element: <SettingsRoutes /> },
              { path: "/settings", element: <SettingsRoutes /> },
              {
                path: "/schedule",
                element: <ScheduleLayout />, // 👈 previously was <ScheduleRouter />
                children: [
                  {
                    // Planning means looking ahead - today is already on the
                    // home page
                    index: true, // same as path: ""
                    element: <Navigate to="week" replace />,
                  },
                  {
                    path: "today/:date?",
                    element: <TodayPage />,
                  },
                  {
                    path: "plan",
                    element: <TaskListSchedule />,
                  },
                  {
                    path: "week/:date?",
                    element: <WeekPage />,
                  },

                  {
                    path: "calendars",
                    element: <CalendarsPage />,
                  },
                  {
                    path: "routines",
                    element: <RoutinesPage />,
                  },
                  {
                    path: "*",
                    element: <p>Not yet implemented</p>,
                  },
                ],
              },
              {
                path: "cooperate",
                element: <DelegateLayout />,
                children: [
                  {
                    index: true, // default path: "/cooperate"
                    element: <DelegateContent />,
                  },
                  {
                    path: "requests", // path: "/cooperate/requests"
                    element: <RequestsContent />,
                  },
                ],
              },
              {
                path: "collect",
                element: <CollectRouter></CollectRouter>,
                children: [
                  {
                    index: true,
                    element: <CollectionList />,
                  },
                ],
              },
              {
                path: "projects",
                element: <ProjectsRouter></ProjectsRouter>,
                children: [
                  {
                    index: true,
                    element: <ProjectTasks></ProjectTasks>,
                  },
                ],
              },
              {
                path: "reflect",
                element: <ArchiveRouter />,
                children: [
                  {
                    index: true,
                    element: <ReflectPage />,
                  },
                  {
                    path: "visibility",
                    element: <ArchivePage />,
                  },
                  {
                    path: "collections",
                    element: <CollectionList />,
                  },
                  {
                    path: "deleted",
                    element: <DeletedPage />,
                  },
                  {
                    path: "completed",
                    element: <CompletedPage />,
                  },
                ],
              },
              { path: "schedule-item/*", element: <CalendarItemPage /> },
              {
                path: "/task/:taskId",
                element: <TaskLayout />, // 👈 new layout
                children: [
                  {
                    index: true, // 👈 this is the default `/task/:id`
                    element: <TaskPageInfo />,
                  },
                  {
                    path: "do",
                    element: <TaskPageDo />,
                  },
                  {
                    path: "schedule",
                    element: <TaskPageSchedule />,
                  },
                  {
                    path: "cooperation",
                    element: <TaskPageTogether />,
                  },
                  {
                    path: "collect",
                    element: <TaskPageCollect />,
                  },
                  {
                    path: "*",
                    element: <p>Task subpage not found</p>,
                  },
                ],
              },
              { path: "request/:requestId/*", element: <RequestRoutes /> },
              { path: "roles", element: <RolesPage /> },
              {
                path: "objectives",
                element: <PersonalObjectivesLayout />,
                children: [
                  { index: true, element: <ObjectivesPage owner="personal" /> },
                  {
                    path: ":objectiveId",
                    element: <ObjectiveDetailPage owner="personal" />,
                  },
                ],
              },
              {
                path: "organization/:organizationId",
                element: <OrganizationLayout />,
                children: [
                  // Start page: is the role on course?
                  {
                    index: true,
                    element: <RoleOverviewPage owner="organization" />,
                  },
                  {
                    path: "projects",
                    element: <RoleProjectsPage owner="organization" />,
                  },
                  {
                    path: "objectives",
                    element: <ObjectivesPage owner="organization" />,
                  },
                  {
                    path: "objectives/:objectiveId",
                    element: <ObjectiveDetailPage owner="organization" />,
                  },
                  {
                    path: "team",
                    element: <RoleTeamPage owner="organization" />,
                  },
                  // Old paths of the merged tabs
                  {
                    path: "groups",
                    element: <Navigate to="../team" replace />,
                  },
                  {
                    path: "members",
                    element: <Navigate to="../team" replace />,
                  },
                  { path: "settings", element: <OrganizationAdminPage /> },
                ],
              },
              {
                path: "group/:groupId",
                element: <GroupLayout />,
                children: [
                  // Start page: is the role on course?
                  { index: true, element: <RoleOverviewPage owner="group" /> },
                  {
                    path: "projects",
                    element: <RoleProjectsPage owner="group" />,
                  },
                  {
                    path: "objectives",
                    element: <ObjectivesPage owner="group" />,
                  },
                  {
                    path: "objectives/:objectiveId",
                    element: <ObjectiveDetailPage owner="group" />,
                  },
                  { path: "team", element: <RoleTeamPage owner="group" /> },
                  // Old paths of the merged tabs
                  {
                    path: "groups",
                    element: <Navigate to="../team" replace />,
                  },
                  {
                    path: "members",
                    element: <Navigate to="../team" replace />,
                  },
                  { path: "settings", element: <GroupSettings /> },
                ],
              },

              { path: "verify-account", element: <VerifiyAccountPage /> },
              {
                path: "collection/:collectionId",
                element: <CollectionRoutes />,
              },
              {
                path: "project/:projectId",
                element: <ProjectRoutes />,
                children: [
                  { index: true, element: <ProjectHome /> },
                  {
                    path: "kanban",
                    element: <KanbanPage></KanbanPage>,
                  },
                  {
                    path: "together",
                    element: <ProjectTogetherPage></ProjectTogetherPage>,
                  },
                  {
                    path: "timeline",
                    element: <ProjectPageTimeline></ProjectPageTimeline>,
                  },
                  {
                    path: "settings",
                    element: <ProjectPageSettings></ProjectPageSettings>,
                  },
                ],
              },

              {
                path: "completed-task/:completedTaskId/*",
                element: <ArchivedTaskPage type={"COMPLETED"} />,
              },
              {
                path: "deleted-task/:completedTaskId/*",
                element: <ArchivedTaskPage type={"DELETED"} />,
              },
              { path: "profile/*", element: <ProfilePage /> },
              { path: "user/:userName/*", element: <UserPage /> },
              { path: "*", element: <DaexWall /> },
            ],
          },
        ],
      },
    ],
    {
      future: {
        v7_relativeSplatPath: true,
        v7_startTransition: true,
      } as any,
    },
  );
}

export default makeRouter;
